import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, Link as RouterLink } from "react-router-dom";
import toast from "react-hot-toast";
import {
  Box,
  TextField,
  Button,
  Typography,
  Link,
  InputAdornment,
  IconButton,
  CircularProgress,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import client from "../../api/client";
import AuthShell from "./AuthShell";

const RESEND_SECONDS = 60;

/**
 * Step 2 of password reset: enter the emailed 6-digit code + a new password.
 * Email arrives via router state from ForgotPasswordPage (editable if reached
 * directly, e.g. an old bookmarked link).
 */
const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState(location.state?.email || "");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !/^\d{6}$/.test(code)) {
      toast.error("Enter the 6-digit code from your email");
      return;
    }
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    setSubmitting(true);
    try {
      await client.post("/auth/reset-password", {
        email: email.trim().toLowerCase(),
        code,
        password,
      });
      toast.success("Password reset — please sign in");
      navigate("/login", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.error || "Reset failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (cooldown > 0 || !email) return;
    try {
      await client.post("/auth/resend-otp", { email: email.trim().toLowerCase(), purpose: "reset" });
      toast.success("A new code has been sent");
      setCooldown(RESEND_SECONDS);
    } catch (err) {
      if (err?.response?.status === 429) setCooldown(RESEND_SECONDS);
      toast.error(err?.response?.data?.error || "Could not resend the code");
    }
  };

  return (
    <AuthShell>
      <Box sx={{ textAlign: "center", mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: "secondary.main" }}>
          Reset password
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
          Enter the code we emailed{email ? ` to ${email}` : ""} and a new password
        </Typography>
      </Box>

      <Box component="form" onSubmit={handleSubmit} noValidate>
        {!location.state?.email && (
          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
            required
            autoComplete="email"
            sx={{ mb: 2 }}
          />
        )}
        <TextField
          label="Verification code"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
          fullWidth
          required
          autoFocus
          inputProps={{ inputMode: "numeric", maxLength: 6, style: { letterSpacing: "0.4em", fontWeight: 600 } }}
          sx={{ mb: 2 }}
        />
        <TextField
          label="New password"
          type={showPassword ? "text" : "password"}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          fullWidth
          required
          autoComplete="new-password"
          helperText="At least 8 characters"
          sx={{ mb: 2 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword((s) => !s)}
                  edge="end"
                  aria-label="toggle password visibility"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          label="Confirm new password"
          type={showPassword ? "text" : "password"}
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          fullWidth
          required
          autoComplete="new-password"
          sx={{ mb: 2 }}
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          color="secondary"
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : null}
          sx={{ py: 1.25 }}
        >
          {submitting ? "Resetting…" : "Reset password"}
        </Button>
      </Box>

      <Typography variant="body2" sx={{ textAlign: "center", mt: 3, color: "text.secondary" }}>
        Didn't get a code?{" "}
        <Link
          component="button"
          type="button"
          onClick={handleResend}
          disabled={cooldown > 0}
          underline="hover"
          sx={{ fontWeight: 600, verticalAlign: "baseline" }}
        >
          {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
        </Link>
        {"  ·  "}
        <Link component={RouterLink} to="/login" underline="hover" sx={{ fontWeight: 600 }}>
          Back to sign in
        </Link>
      </Typography>
    </AuthShell>
  );
};

export default ResetPasswordPage;
